import { connectFunctionsEmulator } from "firebase/functions";
import { calculateAllProgramsEligibilityWithPrerequisites } from './localEligibilityCalculator';
import { getCategoryIconHTML } from './categoryIcons';
import { generateEligibilityIconHTML } from './eligibilityIconToHTML';

export interface ParsedButton {
  id: string;
  label: string;
}

export interface ParsedProgram {
  name: string;
  id: string;
  url: string;
  eligibility?: {
    isEligible: boolean | null;
    isOnRightPath?: string;
    calculatedAverage?: number;
    programAverage?: number;
    missingCourses?: string[];
    gradeIssues?: string[];
    alertMessage?: string;
    nextYearSuggestion?: string;
    coursesUsedInAverage?: string[];
  };
}

export interface ParsedSchool {
  name: string;
  id: string;
  url: string;
}

export interface ParsedCategory {
  name: string;
  url: string;
}

export interface ParsedEvent {
  name: string;
  id: string;
  url: string;
}

export interface ParsedTour {
  text: string;
  url: string;
}

export interface ParsedMessage {
  cleanContent: string;
  buttons: ParsedButton[];
  programs: ParsedProgram[];
  schools: ParsedSchool[];
  categories: ParsedCategory[];
  events: ParsedEvent[];
  tours: ParsedTour[];
}

export type MessageToken = 
  | { type: 'text'; content: string }
  | { type: 'program'; program: ParsedProgram }
  | { type: 'school'; school: ParsedSchool }
  | { type: 'category'; category: ParsedCategory }
  | { type: 'event'; event: ParsedEvent }
  | { type: 'tour'; tour: ParsedTour }
  | { type: 'button'; button: ParsedButton };

export interface ParsedMessageWithTokens {
  cleanContent: string;
  buttons: ParsedButton[];
  programs: ParsedProgram[];
  schools: ParsedSchool[];
  categories: ParsedCategory[];
  events: ParsedEvent[];
  tours: ParsedTour[];
  tokens: MessageToken[];
}

/**
 * Parse various markup elements from message text and return clean content with parsed elements
 * @param content - The raw message content that may contain [[button]], ![P|...], ![S|...], ![C|...] markup
 * @returns Object containing cleanContent, buttons, programs, schools, and categories arrays
 */
export function parseMessageContent(content: string): ParsedMessage {
  const buttons: ParsedButton[] = [];
  const programs: ParsedProgram[] = [];
  const schools: ParsedSchool[] = [];
  const categories: ParsedCategory[] = [];
  const events: ParsedEvent[] = [];
  const tours: ParsedTour[] = [];
  
  // Regular expression to match [[button label]] patterns
  const buttonRegex = /\[\[([^\]]+)\]\]/g;
  let match;
  let updatedContent = content;

  // Extract all button labels and remove from content
  while ((match = buttonRegex.exec(content)) !== null) {
    buttons.push({
      label: match[1].trim(), 
      id: `btn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    });
    content = content.replace(match[0], '');
  }

  // Regular expression to match ![P|Program Name|Program ID] patterns
  const programRegex = /!\[P\|([^|]+)\|([^\]]+)\]/gs;
  
  // Extract all programs and transform content
  while ((match = programRegex.exec(content)) !== null) {
    const programName = match[1].trim();
    const programId = match[2].trim();
    
    programs.push({
      name: programName,
      id: programId,
      url: `/programs/${programId}?fromEdvisor=1`,
    });

    // Replace the match with HTML link
    const htmlLink = `<a href="#" onclick="console.log('🔍 Program link clicked:', '${programId}'); if(window.edvisorMessages&&window.edvisorThreadId&&window.edvisorUserId){console.log('🔍 Saving Edvisor context before navigation'); const edvisorContext={messages:window.edvisorMessages,threadId:window.edvisorThreadId,userId:window.edvisorUserId,timestamp:Date.now()};sessionStorage.setItem('edvisorContext',JSON.stringify(edvisorContext));sessionStorage.setItem('fromEdvisor','true');} else {console.log('🔍 Window object not available:', {hasMessages:!!window.edvisorMessages,hasThreadId:!!window.edvisorThreadId,hasUserId:!!window.edvisorUserId});} window.location.href='/programs/${programId}?fromEdvisor=1';return false;"><strong>📚 ${programName}</strong></a>`;
    content = content.replace(match[0], htmlLink);
  }

  // Regular expression to match ![S|School Name|School ID] patterns
  const schoolRegex = /!\[S\|([^|]+)\|([^\]]+)\]/gs;
  
  // Extract all schools and transform content
  while ((match = schoolRegex.exec(content)) !== null) {
    const schoolName = match[1].trim();
    const schoolId = match[2].trim();
    
    schools.push({
      name: schoolName,
      id: schoolId,
      url: `/schools/${schoolId}?fromEdvisor=1`,
    });

    // Replace the match with HTML link (keeping emoji for this function since it doesn't have access to school data)
    const htmlLink = `<a href="#" onclick="console.log('🔍 School link clicked:', '${schoolId}'); if(window.edvisorMessages&&window.edvisorThreadId&&window.edvisorUserId){console.log('🔍 Saving Edvisor context before navigation'); const edvisorContext={messages:window.edvisorMessages,threadId:window.edvisorThreadId,userId:window.edvisorUserId,timestamp:Date.now()};sessionStorage.setItem('edvisorContext',JSON.stringify(edvisorContext));sessionStorage.setItem('fromEdvisor','true');} else {console.log('🔍 Window object not available:', {hasMessages:!!window.edvisorMessages,hasThreadId:!!window.edvisorThreadId,hasUserId:!!window.edvisorUserId});} window.location.href='/schools/${schoolId}?fromEdvisor=1';return false;"><strong>🏫 ${schoolName}</strong></a>`;
    content = content.replace(match[0], htmlLink);
  }

  // Regular expression to match ![C|Category Name] patterns
  const categoryRegex = /!\[C\|([^\]]+)\]/gs;
  
  // Extract all categories and transform content
  while ((match = categoryRegex.exec(content)) !== null) {
    const categoryName = match[1].trim();
    const encodedCategoryName = encodeURIComponent(categoryName);
    
    categories.push({
      name: categoryName,
      url: `/programs?category=${encodedCategoryName}&fromEdvisor=1`,
    });

    // Replace the match with HTML link
    const htmlLink = `<a href="#" onclick="if(window.edvisorMessages&&window.edvisorThreadId&&window.edvisorUserId){const edvisorContext={messages:window.edvisorMessages,threadId:window.edvisorThreadId,userId:window.edvisorUserId,timestamp:Date.now()};sessionStorage.setItem('edvisorContext',JSON.stringify(edvisorContext));sessionStorage.setItem('fromEdvisor','true');}window.location.href='/programs?category=${encodedCategoryName}&fromEdvisor=1';return false;"><strong>${categoryName}</strong></a>`;
    content = content.replace(match[0], htmlLink);
  }

  // Regular expression to match ![E|Event Name|Event ID] patterns
  const eventRegex = /!\[E\|([^|]+)\|([^\]]+)\]/gs;
  
  // Extract all events and transform content
  while ((match = eventRegex.exec(content)) !== null) {
    const eventName = match[1].trim();
    const eventId = match[2].trim();
    
    events.push({
      name: eventName,
      id: eventId,
      url: `/events/${eventId}?fromEdvisor=1`,
    });

    // Replace the match with HTML link
    const htmlLink = `<a href="#" onclick="if(window.edvisorMessages&&window.edvisorThreadId&&window.edvisorUserId){const edvisorContext={messages:window.edvisorMessages,threadId:window.edvisorThreadId,userId:window.edvisorUserId,timestamp:Date.now()};sessionStorage.setItem('edvisorContext',JSON.stringify(edvisorContext));sessionStorage.setItem('fromEdvisor','true');}window.location.href='/events/${eventId}?fromEdvisor=1';return false;"><strong>🎉 ${eventName}</strong></a>`;
    content = content.replace(match[0], htmlLink);
  }

  // Regular expression to match ![V|Tour Text|URL] patterns
  const tourRegex = /!\[V\|([^|]+)\|([^\]]+)\]/gs;
  
  // Extract all tours and transform content
  while ((match = tourRegex.exec(content)) !== null) {
    const tourText = match[1].trim();
    const tourURL = match[2].trim();
    
    tours.push({
      text: tourText,
      url: tourURL,
    });

    // Determine icon based on tour type
    const isCampusVisit = tourText.toLowerCase().includes('campus visit') || tourText.toLowerCase().includes('book campus');
    const icon = isCampusVisit ? '🚗' : '🎥';

    // Replace the match with HTML link for direct navigation
    const htmlLink = `<a href="${tourURL}" target="_blank" rel="noopener noreferrer" onclick="event.preventDefault(); event.stopPropagation(); window.open('${tourURL}', '_blank', 'noopener,noreferrer'); return false;"><strong>${icon} ${tourText}</strong></a>`;
    content = content.replace(match[0], htmlLink);
  }

  // Remove all markup from content
  let cleanContent = content.trim();

  return {
    cleanContent,
    buttons,
    programs,
    schools,
    categories,
    events,
    tours,
  };
}

/**
 * Check if content contains any markup (buttons, programs, schools, categories, events, or tours)
 * @param content - The content to check
 * @returns True if content contains any markup patterns
 */
export function hasMarkup(content: string): boolean {
  const buttonRegex = /\[\[([^\]]+)\]\]/gs;
  const programRegex = /!\[P\|([^|]+)\|([^\]]+)\]/gs;
  const schoolRegex = /!\[S\|([^|]+)\|([^\]]+)\]/gs;
  const categoryRegex = /!\[C\|([^\]]+)\]/gs;
  const eventRegex = /!\[E\|([^|]+)\|([^\]]+)\]/gs;
  const tourRegex = /!\[V\|([^|]+)\|([^\]]+)\]/gs;
  
  return buttonRegex.test(content) || programRegex.test(content) || schoolRegex.test(content) || categoryRegex.test(content) || eventRegex.test(content) || tourRegex.test(content);
}

/**
 * Check if content contains button markup (for backward compatibility)
 * @param content - The content to check
 * @returns True if content contains [[button]] patterns
 */
export function hasButtonMarkup(content: string): boolean {
  const buttonRegex = /\[\[([^\]]+)\]\]/gs;
  return buttonRegex.test(content);
}

export interface InlineElement {
  type: 'text' | 'program' | 'school' | 'category' | 'button';
  content?: string;
  program?: ParsedProgram;
  school?: ParsedSchool;
  category?: ParsedCategory;
  button?: ParsedButton;
}

/**
 * Parse message content and return HTML for direct injection
 * This preserves the original positions of schools, programs, and categories within the text
 * @param content - The raw message content that may contain [[button]], ![P|...], ![S|...], ![C|...] markup
 * @param programsData - Optional array of programs with eligibility data
 * @param userGrade - Optional user grade level for eligibility indicators
 * @param userCourses - Optional user courses data for eligibility calculation
 * @param prerequisites - Optional prerequisites data for eligibility calculation
 * @returns HTML string that can be injected directly into DOM
 */
export function parseMessageContentAsHTML(
  content: string, 
  programsData?: any[], // Accept any array (Program[] or ParsedProgram[])
  userGrade?: string,
  userCourses?: any,
  prerequisites?: any,
  schoolsData?: any[] // Accept any array (School[])
): string {
  let workingContent = content;
  let elementIndex = 0;

  // Process programs first
  const programRegex = /!\[P\|([^|]+)\|([^\]]+)\]/gs;
  let match;
  while ((match = programRegex.exec(content)) !== null) {
    const programName = match[1].trim();
    const programId = match[2].trim();
    
    // Find program data with eligibility if available
    const programData = programsData?.find(p => p.id === programId);
    
    // Use eligibility status icon instead of category icon
    let statusIcon = '📚 '; // Default fallback
    
    if (programData?.eligibility) {
      // Use React component placeholder for eligibility icon
      console.log('🎨 MessageParser: Using eligibility data for program', {
        programId,
        programName,
        eligibility: programData.eligibility,
        userGrade: userGrade || '10',
        category: programData.category
      });
      statusIcon = `__REACT_ELIGIBILITY_ICON_${programId}__`;
    } else {
      // Fallback to React component placeholder even when no eligibility data
      console.log('🎨 MessageParser: No eligibility data, using React placeholder for program', {
        programId,
        programName,
        hasProgramData: !!programData,
        hasEligibility: !!programData?.eligibility
      });
      statusIcon = `__REACT_ELIGIBILITY_ICON_${programId}__`;
    }
    
    // Create HTML for program chip with eligibility status icon (no separate eligibility indicator needed)
    const programHTML = `<span class="program-chip" data-program-id="${programId}" data-program-name="${programName}">${statusIcon}<a href="#" style="color: #1976d2 !important; text-decoration: underline !important;" onclick="console.log('🔍 Program chip clicked:', '${programId}'); if(window.edvisorMessages&&window.edvisorThreadId&&window.edvisorUserId){console.log('🔍 Saving Edvisor context before navigation'); const edvisorContext={messages:window.edvisorMessages,threadId:window.edvisorThreadId,userId:window.edvisorUserId,timestamp:Date.now()};sessionStorage.setItem('edvisorContext',JSON.stringify(edvisorContext));sessionStorage.setItem('fromEdvisor','true');} else {console.log('🔍 Window object not available:', {hasMessages:!!window.edvisorMessages,hasThreadId:!!window.edvisorThreadId,hasUserId:!!window.edvisorUserId});} window.location.href='/programs/${programId}?fromEdvisor=1';return false;">${programName}</a></span>`;
    
    workingContent = workingContent.replace(match[0], programHTML);
    elementIndex++;
  }

  // Process schools
  const schoolRegex = /!\[S\|([^|]+)\|([^\]]+)\]/gs;
  while ((match = schoolRegex.exec(content)) !== null) {
    const schoolName = match[1].trim();
    const schoolId = match[2].trim();
    
    // Find school data to get the results image
    const schoolData = schoolsData?.find(s => s.id === schoolId);
    const schoolImage = schoolData?.resultsImage;
    
    // Create HTML for school chip with actual school image or fallback to emoji
    const schoolIcon = schoolImage 
      ? `<img src="${schoolImage}" alt="${schoolName}" style="width: 20px; height: 20px; margin-right: 4px; vertical-align: middle; border-radius: 50%;" />`
      : '🏫 ';
    
    const schoolHTML = `<span class="school-chip" data-school-id="${schoolId}" data-school-name="${schoolName}"><span class="logo-wrapper">${schoolIcon}</span><a href="#" style="color: #1976d2 !important; text-decoration: underline !important;" onclick="console.log('🔍 School chip clicked:', '${schoolId}'); if(window.edvisorMessages&&window.edvisorThreadId&&window.edvisorUserId){console.log('🔍 Saving Edvisor context before navigation'); const edvisorContext={messages:window.edvisorMessages,threadId:window.edvisorThreadId,userId:window.edvisorUserId,timestamp:Date.now()};sessionStorage.setItem('edvisorContext',JSON.stringify(edvisorContext));sessionStorage.setItem('fromEdvisor','true');} else {console.log('🔍 Window object not available:', {hasMessages:!!window.edvisorMessages,hasThreadId:!!window.edvisorThreadId,hasUserId:!!window.edvisorUserId});} window.location.href='/schools/${schoolId}?fromEdvisor=1';return false;">${schoolName}</a></span>`;
    
    workingContent = workingContent.replace(match[0], schoolHTML);
    elementIndex++;
  }

  // Process categories
  const categoryRegex = /!\[C\|([^\]]+)\]/gs;
  while ((match = categoryRegex.exec(content)) !== null) {
    const categoryName = match[1].trim();
    const encodedCategoryName = encodeURIComponent(categoryName);
    
    // Create HTML for category chip with icon
    const categoryIconHTML = getCategoryIconHTML(categoryName, 20);
    const categoryHTML = `<span class="category-chip" data-category-name="${categoryName}" onclick="if(window.edvisorMessages&&window.edvisorThreadId&&window.edvisorUserId){const edvisorContext={messages:window.edvisorMessages,threadId:window.edvisorThreadId,userId:window.edvisorUserId,timestamp:Date.now()};sessionStorage.setItem('edvisorContext',JSON.stringify(edvisorContext));sessionStorage.setItem('fromEdvisor','true');}window.location.href='/programs?category=${encodedCategoryName}&fromEdvisor=1';return false;">${categoryIconHTML}${categoryName}</span>`;
    
    workingContent = workingContent.replace(match[0], categoryHTML);
    elementIndex++;
  }

  // Process events
  const eventRegex = /!\[E\|([^|]+)\|([^\]]+)\]/gs;
  while ((match = eventRegex.exec(content)) !== null) {
    const eventName = match[1].trim();
    const eventId = match[2].trim();
    
    // Create HTML for event chip with icon
    const eventHTML = `<span class="event-chip" data-event-id="${eventId}" data-event-name="${eventName}" onclick="if(window.edvisorMessages&&window.edvisorThreadId&&window.edvisorUserId){const edvisorContext={messages:window.edvisorMessages,threadId:window.edvisorThreadId,userId:window.edvisorUserId,timestamp:Date.now()};sessionStorage.setItem('edvisorContext',JSON.stringify(edvisorContext));sessionStorage.setItem('fromEdvisor','true');}window.location.href='/events/${eventId}?fromEdvisor=1';return false;">🎉 ${eventName}</span>`;
    
    workingContent = workingContent.replace(match[0], eventHTML);
    elementIndex++;
  }

  // Process tour links
  const tourRegex = /!\[V\|([^|]+)\|([^\]]+)\]/gs;
  while ((match = tourRegex.exec(content)) !== null) {
    const tourText = match[1].trim();
    const tourURL = match[2].trim();
    
    // Determine icon based on tour type
    const isCampusVisit = tourText.toLowerCase().includes('campus visit') || tourText.toLowerCase().includes('book campus');
    const icon = isCampusVisit ? '🚗' : '🎥';
    
    // Create HTML for tour chip with icon and direct URL navigation
    const tourHTML = `<span class="tour-chip" data-tour-text="${tourText}" data-tour-url="${tourURL}" onclick="event.preventDefault(); event.stopPropagation(); window.open('${tourURL}', '_blank', 'noopener,noreferrer'); return false;">${icon} ${tourText}</span>`;
    
    workingContent = workingContent.replace(match[0], tourHTML);
    elementIndex++;
  }

  // Process markdown links first (to avoid duplication with URL processing)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  while ((match = markdownLinkRegex.exec(content)) !== null) {
    const linkText = match[1];
    const linkUrl = match[2];
    // Create HTML for markdown link that opens in new tab
    const linkHTML = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" class="external-link">${linkText}</a>`;
    workingContent = workingContent.replace(match[0], linkHTML);
    elementIndex++;
  }

  // Process standalone URLs (must be done after markdown links to avoid conflicts)
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  while ((match = urlRegex.exec(content)) !== null) {
    const url = match[1];
    // Skip if this URL is already inside an HTML tag
    if (workingContent.includes(`href="${url}"`) || workingContent.includes(`>${url}<`)) {
      continue;
    }
    // Create HTML for external link that opens in new tab
    const linkHTML = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="external-link">${url}</a>`;
    workingContent = workingContent.replace(match[0], linkHTML);
    elementIndex++;
  }

  // Remove old button markup from content (clean up [[button]] text)
  const buttonRegex = /\[\[([^\]]+)\]\]/gs;
  while ((match = buttonRegex.exec(content)) !== null) {
    // Remove the button markup entirely - don't render as buttons or text
    workingContent = workingContent.replace(match[0], '');
    elementIndex++;
  }

  // Now process the content for basic formatting
  return processBasicFormatting(workingContent);
}


/**
 * Process basic text formatting (bold, lists, line breaks)
 */
function processBasicFormatting(content: string): string {
  let html = content;

  // Convert line breaks to <br> tags
  html = html.replace(/\n/g, '<br>');

  // Handle bold text (simple **text** format)
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Handle bullet points
  html = html.replace(/^- (.+)$/gm, '<li style="margin-left: 16px; margin-bottom: 4px;">$1</li>');

  // Handle numbered lists
  html = html.replace(/^(\d+)\. (.+)$/gm, '<li style="margin-left: 16px; margin-bottom: 4px;">$2</li>');

  return html;
}
